// src/App.js
import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Providers de contexte
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LocationProvider } from './contexts/LocationContext';
import { MaintenanceProvider, useMaintenanceContext } from './contexts/MaintenanceContext';

// Composant de protection des routes
import ProtectedRoute from './components/ProtectedRoute';

// ✨ NOUVEAU : Composant FloatingCartButton
import FloatingCartButton from './components/ui/FloatingCartButton';

// Pages
import SplashPage from './pages/SplashPage';
import LocationPage from './pages/LocationPage';
import MainCategoryPage from './pages/MainCategoryPage';
import SubCategoryPage from './pages/SubCategoryPage';
import ProductListPage from './pages/ProductListPage';
import BeerPage from './pages/BeerPage';
import WinePage from './pages/WinePage';
import SpiritsPage from './pages/SpiritsPage';
import BeersPage from './pages/BeersPage';
import CartPage from './pages/CartPage';
import DeliveryFormPage from './pages/DeliveryFormPage';
import OrderPage from './pages/OrderPage';
import NotFoundPage from './pages/NotFoundPage';
import RegisterPage from './pages/RegisterPage';
import PrototypeCategoryPage from './pages/PrototypeCategoryPage';
import WelcomePage from './pages/WelcomePage';
import PhoneVerificationPage from './pages/PhoneVerificationPage';
import MaintenancePage from './pages/MaintenancePage';
import MyOrdersPage from './pages/MyOrdersPage';
import MyAccountPage from './pages/MyAccountPage';
// ✨ NOUVEAU : Page d'évaluation post-livraison
import EvaluationPage from './pages/EvaluationPage';

// Style global
import './styles/index.css';

/**
 * Composant wrapper pour gérer la maintenance
 * Vérifie à chaque changement de route
 */
const MaintenanceWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInMaintenance, maintenanceInfo, forceCheck } = useMaintenanceContext();
  const previousPathRef = useRef(location.pathname);

  // Vérifier UNIQUEMENT lors du changement de pathname
  useEffect(() => {
    // Ne vérifier que si le pathname a vraiment changé
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;
      forceCheck();
    }
  }, [location.pathname]); // ⚠️ NE PAS inclure forceCheck dans les dépendances

  // Rediriger vers la page de maintenance si nécessaire
  useEffect(() => {
    // ✨ MODIFICATION : Exclure la page d'évaluation de la redirection maintenance
    const isEvaluationPage = location.pathname.startsWith('/evaluation');
    
    if (isInMaintenance && location.pathname !== '/maintenance' && !isEvaluationPage) {
      console.log('🔧 Redirection vers maintenance');
      navigate('/maintenance', { replace: true });
    } else if (!isInMaintenance && location.pathname === '/maintenance') {
      console.log('✅ Maintenance terminée, retour à l\'accueil');
      navigate('/', { replace: true });
    }
  }, [isInMaintenance, location.pathname, navigate]);

  // Si en maintenance, afficher seulement la page de maintenance
  // ✨ MODIFICATION : Permettre l'accès à la page d'évaluation même en maintenance
  if (isInMaintenance && !location.pathname.startsWith('/evaluation')) {
    return (
      <MaintenancePage 
        message={maintenanceInfo.message}
        estimatedTime={maintenanceInfo.estimatedTime}
      />
    );
  }

  return children;
};

const AppRoutes = () => {
  // Vérifier les mises à jour de version
  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now());
        const data = await response.json();
        
        const currentVersion = localStorage.getItem('app_version');
        
        if (currentVersion && currentVersion !== data.version) {
          console.log(`Nouvelle version détectée: ${currentVersion} -> ${data.version}`);
          
          if (data.forceUpdate || window.confirm('Une nouvelle version est disponible. Voulez-vous mettre à jour maintenant ?')) {
            localStorage.setItem('app_version', data.version);
            
            // Nettoyer tous les caches
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
              );
            }
            
            // Désenregistrer le service worker
            if ('serviceWorker' in navigator) {
              const registrations = await navigator.serviceWorker.getRegistrations();
              for (let registration of registrations) {
                await registration.unregister();
              }
            }
            
            window.location.reload(true);
          }
        } else if (!currentVersion) {
          localStorage.setItem('app_version', data.version);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de version:', error);
      }
    };

    checkForUpdates();
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Initialiser la PWA
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('SW registered:', registration);
        })
        .catch(error => {
          console.log('SW registration failed:', error);
        });
    }
  }, []);

  return (
    <AuthProvider>
      <LocationProvider>
        <CartProvider>
          {/* ✨ Wrapper de maintenance - Gère la redirection automatique */}
          <MaintenanceWrapper>
            <FloatingCartButton />
            
            <Routes>
              {/* ✨ NOUVEAU : Route de maintenance */}
              <Route path="/maintenance" element={<MaintenancePage />} />
              
              {/* ✨ NOUVEAU : Route d'évaluation post-livraison (publique) */}
              <Route path="/evaluation/:idCommande" element={<EvaluationPage />} />
              
              {/* Routes publiques */}
              <Route path="/" element={<SplashPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/location" element={<LocationPage />} />
              <Route path="/categories" element={<MainCategoryPage />} />
              <Route path="/subcategories" element={<SubCategoryPage />} />
              <Route path="/beer" element={<BeerPage />} />
              <Route path="/wine" element={<WinePage />} />
              <Route path="/spirits" element={<SpiritsPage />} />
              <Route path="/beers" element={<BeersPage />} />
              <Route path="/products/:category" element={<ProductListPage />} />
              
              {/* Routes protégées */}
              <Route path="/prototype-categories" element={
                <ProtectedRoute>
                  <PrototypeCategoryPage />
                </ProtectedRoute>
              } />
              
              <Route path="/welcome-page" element={
                <ProtectedRoute>
                  <WelcomePage />
                </ProtectedRoute>
              } />
              
              <Route path="/cart" element={
                <ProtectedRoute>
                  <CartPage />
                </ProtectedRoute>
              } />
              
              <Route path="/delivery" element={
                <ProtectedRoute>
                  <DeliveryFormPage />
                </ProtectedRoute>
              } />
              
              <Route path="/phone-verification/:orderId" element={
                <ProtectedRoute>
                  <PhoneVerificationPage />
                </ProtectedRoute>
              } />
              
              <Route path="/order/:orderId" element={
                <ProtectedRoute>
                  <OrderPage />
                </ProtectedRoute>
              } />
              
              {/* ✨ NOUVELLES ROUTES : Mes commandes et Mon compte */}
              <Route path="/my-orders" element={
                <ProtectedRoute>
                  <MyOrdersPage />
                </ProtectedRoute>
              } />
              
              <Route path="/my-account" element={
                <ProtectedRoute>
                  <MyAccountPage />
                </ProtectedRoute>
              } />
              
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </MaintenanceWrapper>
        </CartProvider>
      </LocationProvider>
    </AuthProvider>
  );
};

const App = () => {
  return (
    <Router>
      <MaintenanceProvider>
        <AppRoutes />
      </MaintenanceProvider>
    </Router>
  );
};
export default App;